import { useEffect, useMemo, useState, useRef } from "react";
import {
  FiBriefcase,
  FiCheckCircle,
  FiXCircle,
  FiTrendingUp,
  FiAward,
  FiCreditCard,
  FiBarChart2,
} from "react-icons/fi";
import { LuIndianRupee } from "react-icons/lu";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import DealsCreate from "./DealsCreate";
import api from "../../api/axios";
import DynamicTable from "../Common/DynamicTable";
import { dealColumns } from "./DealsColumn";
import DealsDrawer from "./DealsDrawer";
import { DEAL_STATUSES } from "../Common/DealStatus";
import DateFilter from "../Common/DateFilter";
import { buildDateFilterParams } from "../../utils/dateFilterUtils";

import "../../assets/css/Deals/DealsList.css";
import "../../assets/css/Common/stats_cards.css";

const DEFAULT_CARDS = {
  total_deals: 0,
  lost_deals: 0,
  won_deals: 0,
  projected_profit: 0,
  profit: 0,
  actual_revenue: 0,
  actual_expense: 0,
  expected_revenue: 0,
};

export default function DealsList() {
  const navigate = useNavigate();
  const location = useLocation();

  const [deals, setDeals] = useState([]);
  const [cards, setCards] = useState(DEFAULT_CARDS);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const [statusFilter, setStatusFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");

  const [selectedDealId, setSelectedDealId] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState("overview");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [dateFilter, setDateFilter] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (location.state?.openDealId) {
      setDrawerTab(location.state.openFollowUp ? "timeline" : "overview");
      openDeal(location.state.openDealId);
    }
  }, [location.state]);

  const fetchData = async (filter = dateFilter) => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const config = {};
      if (token) {
        config.headers = { Authorization: `Bearer ${token}` };
      }
      const params = buildDateFilterParams(filter);
      if (Object.keys(params).length > 0) {
        config.params = params;
      }

      const [dealsRes, cardsRes] = await Promise.all([
        Object.keys(config).length > 0 ? api.get("/deal/", config) : api.get("/deal/"),
        Object.keys(config).length > 0 ? api.get("/deal/cards", config) : api.get("/deal/cards"),
      ]);

      if (currentRequestId !== requestIdRef.current) return;

      setDeals(dealsRes.data || []);
      setCards(cardsRes.data || DEFAULT_CARDS);
    } catch (error) {
      if (currentRequestId !== requestIdRef.current) return;
      console.error("Failed to load deals data:", error);
      setDeals([]);
      setCards(DEFAULT_CARDS);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const canDelete =
    user?.role !== "Sales Executive" &&
    user?.role !== "Sales Manager";

  const fetchDeals = (filter) => fetchData(filter);
  const fetchCards = (filter) => fetchData(filter);

  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    fetchData(newFilter);
  };



  const openDeal = async (id) => {
    const numId = Number(id) || id;
    setSelectedDealId(numId);
    const existing = deals.find((d) => Number(d.id) === Number(numId));
    if (existing) {
      setSelectedDeal(existing);
    }
    setDrawerOpen(true);
    try {
      const res = await api.get(`/deal/${numId}`);
      if (res.data) {
        setSelectedDeal(res.data);
      }
    } catch (err) {
      console.error("Error fetching deal details:", err);
    }
  };

  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === sortedDeals.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(sortedDeals.map((deal) => deal.id));
    }
  };



  const statuses = useMemo(() => {
    const existing = deals.map((deal) => deal.status).filter(Boolean);
    return Array.from(new Set([...DEAL_STATUSES, ...existing]));
  }, [deals]);

  const owners = useMemo(() => {
    return [
      ...new Set(
        deals
          .map((deal) => deal.assigned_user)
          .filter(Boolean)
      ),
    ];
  }, [deals]);

  const stats = useMemo(() => {
    return {
      totalDeals: deals.length,

      activeDeals: deals.filter(
        (d) =>
          !["Won Close", "Won", "Lost", "Deal Lost"].includes(d.status)
      ).length,

      wonDeals: deals.filter(
        (d) => d.status === "Won Close" || d.status === "Won"
      ).length,

      closedDeals: deals.filter(
        (d) => d.status === "Lost" || d.status === "Deal Lost"
      ).length,

      totalRevenue: deals.reduce(
        (sum, d) => sum + Number(d.deal_amount || 0),
        0
      ),

      totalExpenses: deals.reduce(
        (sum, d) => sum + Number(d.actual_expense || 0),
        0
      ),

      projectedProfit: deals.reduce(
        (sum, d) => sum + Number(d.projected_profit || 0),
        0
      ),

      actualProfit: deals.reduce(
        (sum, d) => sum + Number(d.actual_profit || 0),
        0
      ),
    };
  }, [deals]);

  //Filter and sort


  const filteredData = useMemo(() => {
    const safeSearch = (search || "").toLowerCase().trim();
    return deals.filter((deal) => {
      const matchesSearch =
        !safeSearch ||
        deal.title?.toLowerCase().includes(safeSearch) ||
        deal.organization?.name?.toLowerCase().includes(safeSearch) ||
        deal.contact?.first_name?.toLowerCase().includes(safeSearch) ||
        deal.contact?.second_name?.toLowerCase().includes(safeSearch) ||
        deal.status?.toLowerCase().includes(safeSearch) ||
        deal.assigned_user?.toLowerCase().includes(safeSearch) ||
        String(deal.value || "").toLowerCase().includes(safeSearch) ||
        String(deal.id || "").includes(safeSearch);

      const matchesStatus = statusFilter
        ? deal.status === statusFilter
        : true;

      const matchesOwner = ownerFilter
        ? deal.assigned_user === ownerFilter
        : true;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesOwner
      );
    });
  }, [deals, search, statusFilter, ownerFilter]);

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });


  const getSortValue = (deal, key) => {
    switch (key) {
      case "title":
        return deal.title || "";

      case "organization":
        return deal.organization?.name || "";

      case "deal_amount":
      case "actual_expense":
      case "expected_expense":
        return Number(deal[key] || 0);

      case "status":
        return deal.status || "";

      case "owner":
        return deal.assigned_user?.name || deal.creator?.name || "";

      case "created_at":
        return deal.lead?.created_at
          ? new Date(deal.lead.created_at).getTime()
          : 0;

      default:
        return deal[key];
    }
  };



  const sortedDeals = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.key);
      const bVal = getSortValue(b, sortConfig.key);

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "number") {
        return sortConfig.direction === "asc"
          ? aVal - bVal
          : bVal - aVal;
      }

      return sortConfig.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredData, sortConfig]);



  const handleSortChange = (key) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };



  //bulk delete 

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;

    const res = await Swal.fire({
      title: `Delete ${selectedIds.length} deals?`,
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Yes, Delete",
    });

    if (!res.isConfirmed) return;

    try {
      await api.post("/deal/bulk-delete", {
        deal_ids: selectedIds,
      });

      await Swal.fire({
        icon: "success",
        title: "Deleted Successfully",
        timer: 1200,
        showConfirmButton: false,
      });

      setSelectedIds([]);
      fetchDeals();
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Bulk Delete Failed",
      });
    }
  };


  if (loading) {
    return (
      <div className="page-loading">
        Loading Deals...
      </div>
    );
  }
  // {
  //   "total_deals": 4,
  //   "lost_deals": 0,
  //   "won_deals": 2,
  //   "projected_profit": 215000,
  //   "profit": 230000,
  //   "actual_revenue": 445000,
  //   "actual_expense": 115000,
  //   "expected_revenue": 510000
  // }
  return (
    <div className="main-wrapper">

      {/* HEADER */}
      <div className="page-header">
        <h2>Deals</h2>
        <DateFilter value={dateFilter} onChange={handleDateFilterChange} />
      </div>

      {/* STATS */}
      <div className={`stats-grid stats-grid-8${loading ? " refreshing" : ""}`}>

        {/* Total Deals */}
        <div className="stat-card">
          <div className="stat-icon blue">
            <FiBriefcase />
          </div>
          <div>
            <p>Total Deals</p>
            <h2>{cards?.total_deals ?? 0}</h2>
          </div>
        </div>

        {/* Lost Deals */}
        <div className="stat-card">
          <div className="stat-icon red">
            <FiXCircle />
          </div>
          <div>
            <p>Lost Deals</p>
            <h2>{cards?.lost_deals ?? 0}</h2>
          </div>
        </div>

        {/* Won Deals */}
        <div className="stat-card">
          <div className="stat-icon green">
            <FiAward />
          </div>
          <div>
            <p>Won Deals</p>
            <h2>{cards?.won_deals ?? 0}</h2>
          </div>
        </div>

        {/* Projected Profit */}
        <div className="stat-card">
          <div className="stat-icon orange">
            <FiTrendingUp />
          </div>
          <div>
            <p>Projected Profit</p>
            <h2>{Number(cards?.projected_profit || 0).toLocaleString("en-IN")}</h2>
          </div>
        </div>

        {/* Profit */}
        <div className="stat-card">
          <div className="stat-icon green">
            <LuIndianRupee />
          </div>
          <div>
            <p>Profit</p>
            <h2>{Number(cards?.profit || 0).toLocaleString("en-IN")}</h2>
          </div>
        </div>

        {/* Actual Revenue */}
        <div className="stat-card">
          <div className="stat-icon blue">
            <FiBarChart2 />
          </div>
          <div>
            <p>Actual Revenue</p>
            <h2>{Number(cards?.actual_revenue || 0).toLocaleString("en-IN")}</h2>
          </div>
        </div>

        {/* Actual Expense */}
        <div className="stat-card">
          <div className="stat-icon purple">
            <FiCreditCard />
          </div>
          <div>
            <p>Actual Expense</p>
            <h2>{Number(cards?.actual_expense || 0).toLocaleString("en-IN")}</h2>
          </div>
        </div>

        {/* Expected Revenue */}
        <div className="stat-card">
          <div className="stat-icon teal">
            <FiTrendingUp />
          </div>
          <div>
            <p>Expected Revenue</p>
            <h2>{Number(cards?.expected_revenue || 0).toLocaleString("en-IN")}</h2>
          </div>
        </div>


      </div>

      {/* TABLE */}
      <div className="table-card">

        <DynamicTable

          data={sortedDeals}
          columns={dealColumns({
            selectedIds,
            handleSelect,
            handleSelectAll,
            totalRows: sortedDeals.length,
          })}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search deals..."
          addButtonText="Add Deal"
          selectedIds={selectedIds}
          selectedCount={selectedIds.length}
          onRowClick={(id) => {
            setDrawerTab("overview");
            openDeal(id);
          }}
          onBulkDelete={handleBulkDelete}
          sortConfig={sortConfig}
          onSortChange={handleSortChange}
           onSelect={handleSelect}
          canDelete={canDelete}
          storageKey="deals_columns"
          filters={[
            {
              name: "status",
              value: statusFilter,
              onChange: setStatusFilter,
              options: statuses,
              placeholder: "All Status",
            },
            {
              name: "owner",
              value: ownerFilter,
              onChange: setOwnerFilter,
              options: owners,
              placeholder: "All Owners",
            },
          ]}
        />
      </div>

      <DealsCreate
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={fetchDeals}
      />
      <DealsDrawer
        dealId={selectedDealId || selectedDeal?.id}
        deal={selectedDeal || deals.find((d) => Number(d.id) === Number(selectedDealId))}
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerTab("overview");
          setSelectedDeal(null);
        }}
        refreshDeals={fetchDeals}
        initialTab={drawerTab}
      />
    </div>
  );
}